import React, { useMemo, useState } from 'react';
import { Button, Modal } from 'antd';
import { ExclamationCircleOutlined, PlusOutlined } from '@ant-design/icons';
import EvaluationCaseModal from './EvaluationCaseModal';
import EvaluationRow from './EvaluationRow';
import { CaseDraft } from './useCaseEditor';
import { EvaluationCase, EvaluationResult, EMPTY_EVALUATION_RESULT } from './types';
import { mergeCases } from './rows';
import { CaseErrors, NO_CASE_ERRORS } from './utils';

/**
 * The evaluation table: one row per case, carrying its own last verdict.
 *
 * The cases are the form's value and the run is read-only, but they are one
 * table because they are one thing to read — a case beside what the model made
 * of it. They are paired on the declaration rather than on position, so a case
 * edited since the run is flagged instead of quietly borrowing another case's
 * result; see `mergeCases`.
 */
const EvaluationCasesField = ({
    value = [],
    onChange,
    result = EMPTY_EVALUATION_RESULT,
    errors = NO_CASE_ERRORS,
    readOnly = false,
}: {
    /** injected by Form.Item */
    value?: EvaluationCase[];
    onChange?: (next: EvaluationCase[]) => void;
    /** the last run, for the columns on the right */
    result?: EvaluationResult;
    /** what the API said about these cases the last time they were sent */
    errors?: CaseErrors;
    readOnly?: boolean;
}) => {
    const cases = Array.isArray(value) ? value : [];
    const [draft, setDraft] = useState<CaseDraft | null>(null);
    // one open at a time: a declaration is tall, and two push the rest off screen
    const [openKey, setOpenKey] = useState<string | null>(null);

    const rows = useMemo(() => mergeCases(cases, result.results), [cases, result.results]);
    /* Before the first run there is nothing to put in the verdict columns, and
       three columns of "—" is a table pretending to know something. */
    const hasRun = !!result.run_id;

    const commit = (next: EvaluationCase, index: number | null) => {
        onChange?.(index === null ? [...cases, next] : cases.map((item, i) => (i === index ? next : item)));
        setDraft(null);
    };

    const remove = (index: number) => {
        Modal.confirm({
            icon: <ExclamationCircleOutlined />,
            title: `Delete test case ${index + 1}?`,
            content: 'The case is removed when you save the processor.',
            okText: 'Delete',
            okButtonProps: { danger: true },
            onOk() {
                onChange?.(cases.filter((_, i) => i !== index));
                setOpenKey(null);
            },
        });
    };

    return (
        <div className={`aip-tbl${hasRun ? ' aip-tbl--run' : ''}`}>
            {errors.general.length > 0 && (
                <ul className="aip-errors" role="alert">
                    {errors.general.map((message) => (
                        <li key={message}>{message}</li>
                    ))}
                </ul>
            )}

            <div className="aip-tbl__frame">
                {/* the column names, and the one thing the table as a whole can
                    be asked for — a case that is not there yet */}
                <div className="aip-tbl__head" role="presentation">
                    <span className="aip-cell aip-cell--num">#</span>
                    <span className="aip-cell aip-cell--decl">Declaration</span>
                    <span className="aip-cell">Expected</span>
                    {hasRun && <span className="aip-cell">Model said</span>}
                    {hasRun && <span className="aip-cell">Status</span>}
                    <span className="aip-cell aip-cell--acts">
                        {!readOnly && (
                            /* the page's primary button, the same one that saves
                               it — this is the one thing the table as a whole
                               can be asked for */
                            <Button
                                className="aip-btn"
                                type="primary"
                                htmlType="button"
                                icon={<PlusOutlined />}
                                aria-label="Add a test case"
                                onClick={() => setDraft({ index: null })}
                            >
                                Add test case
                            </Button>
                        )}
                    </span>
                    <span className="aip-cell" />
                </div>

                {rows.length === 0 ? (
                    <p className="aip-empty">
                        No test cases yet. Add a declaration and say whether the validator should approve or reject
                        it.
                    </p>
                ) : (
                    <ul className="aip-tbl__rows">
                        {rows.map((row) => (
                            <EvaluationRow
                                key={row.key}
                                row={row}
                                hasRun={hasRun}
                                open={openKey === row.key}
                                toggle={() => setOpenKey((previous) => (previous === row.key ? null : row.key))}
                                readOnly={readOnly}
                                onEdit={(index) => setDraft({ index })}
                                onDelete={remove}
                                errors={row.caseIndex === null ? [] : errors.byIndex[row.caseIndex]}
                            />
                        ))}
                    </ul>
                )}
            </div>

            {/* mounted on a draft and dropped on close: a modal that is never
                opened is a modal that never has to render */}
            {draft && (
                <EvaluationCaseModal cases={cases} draft={draft} onCommit={commit} onCancel={() => setDraft(null)} />
            )}
        </div>
    );
};

export default EvaluationCasesField;
